// src/pages/providers/2_dashboard/components/ProviderEditUserForm.js
//
// Modal form for managing a provider user's profile, used by Support and
// Org. Admin users.  Supports several categories of action, each accessible
// via its own action card:
//
//   1. Edit Details  -- title, name, role, registration, pathway specialisms,
//      and (for password/hybrid orgs) the login email address
//   2. Resend Welcome Email  -- re-send the welcome/setup email to invited
//      staff who have not yet completed first sign-in
//   3. Disable / Enable Account  -- prevent or restore sign-in
//   4. Change Account Type  -- switch between Support, Clinician,
//      Org. Admin, and Org. Admin (Clinical)
//
// Architectural notes:
// - Uses the same Radix UI AlertDialog pattern as ProviderManageInformantsForm.
// - Follows a multi-step state machine: summary -> action-specific screens
//   -> confirmation -> success.
// - Backend operations are performed via Cloud Functions:
//   - Profile fields call `updateProviderUserDetails`; email identity changes
//     call the dedicated `updateProviderUserEmail` Cloud Function (Auth +
//     Firestore + denormalized patient clinicianEmail fan-out + notifications).
//   - Welcome resend calls `resendProviderWelcomeEmail` and returns to summary
//     with a toast (patient-resend parity — no dedicated success step).
//   - Disable/Enable and account-type changes call `disableProviderUser`,
//     `enableProviderUser`, and `changeUserAccountType` respectively.

import React, { useState, useEffect, useContext, useRef } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    XIcon,
    ChevronRightIcon,
    UserIcon,
    IdentificationIcon,
    ClipboardListIcon,
    ExclamationIcon,
    BanIcon,
    SwitchHorizontalIcon,
    MailIcon,
    ShieldExclamationIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolid, CheckIcon } from '@heroicons/react/solid';
import { auth, functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { useAuth } from '../../../../hooks/useAuth';
import { sanitizeNameInput, sanitizeEmailInput } from '../../../../utils/inputSanitization';
import { normaliseProviderRole } from '../../../../utils/providerRole';
import {
    PATHWAY_SPECIALISM_LABELS,
    getEffectiveSpecialisms,
    getSelectablePathwaySpecialisms,
} from '../../../../utils/pathwaySpecialismUtils';
import {
    getUiUserType,
    ACCOUNT_TYPE_OPTIONS,
    isClinicalType,
    isGatedTransition,
    removesAdminStatus,
} from '../../../../utils/accountTypeUtils';
import {
    canEditStaffEmail,
    canResendStaffWelcome,
    canResendStaffWelcomeForTarget,
    getStaffAccountStatus,
    isStaffAccountActive,
    isStaffEmailConfirmReady,
    isStaffEmailEditSsoGated,
    isValidStaffEmailFormat,
    resolveStaffWelcomeFirstSent,
} from '../../../../utils/staffEmailEditUtils';
import { formatDateTime } from '../../../../utils/timestampUtils';
import WarningPanel from '../../../../components/informantManagement/WarningPanel';
import InfoTooltip from '../../../../components/InfoTooltip';
import ResetMfaFlow from '../../../../components/userManagement/ResetMfaFlow';
import useAnalytics from '../../../../hooks/useAnalytics';
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';

/**
 * Shared react-select portal props for dropdowns inside this AlertDialog.
 * See getScrollablePortalTarget for the scroll-lock fix details.
 */
const DIALOG_SELECT_PORTAL_PROPS = {
    classNamePrefix: 'td-select',
    menuPortalTarget: getScrollablePortalTarget(),
    menuPosition: 'fixed',
};

// Field configuration for the edit forms. Reuses the same option sets as the
// account setup config to keep the dropdown choices consistent.
const TITLE_OPTIONS = [
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Prof.', label: 'Prof.' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Miss', label: 'Miss' },
    { value: 'N/A', label: 'N/A' },
];

const REGISTRATION_BODY_OPTIONS = [
    { value: 'GMC', label: 'General Medical Council (GMC)' },
    { value: 'HCPC', label: 'Health and Care Professions Council (HCPC)' },
    { value: 'NMC', label: 'Nursing and Midwifery Council (NMC)' },
    { value: 'GPhC', label: 'General Pharmaceutical Council (GPhC)' },
    { value: 'N/A', label: 'N/A' },
];

function ProviderEditUserForm({ isOpen, onClose, clinician, availableOrgModules = ['adultAdhd'] }) {
    const {
        refreshProviderAccountsData,
        refreshUserDataOnly,
        userData,
    } = useContext(GlobalStateContext);
    const { user, isOrgAdmin, isSupportUser } = useAuth();
    const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();

    // Navigation state: which screen is currently displayed.
    // 'summary' — overview of current details with action cards
    // 'edit-name' / 'edit-role' / 'edit-registration' / 'edit-specialisms' /
    //   'edit-email' — detail-editing screens
    // 'confirm-resend' — confirm before resending staff welcome email
    // 'disable-account' / 'enable-account' / 'edit-account-type' /
    //   'account-type-blocked' — account-management screens
    // 'confirm' — review profile/email changes before submitting
    // 'success' — operation completed successfully
    const [currentStep, setCurrentStep] = useState('summary');

    // Edit form state -- pre-populated from the clinician's current data
    const [editData, setEditData] = useState({});

    // Tracks which category was most recently edited so the confirmation
    // screen can show a contextual heading.
    const [editCategory, setEditCategory] = useState(null);

    // Async operation state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // MFA reset state — managed independently from isSubmitting/submitError
    // because the reset is a standalone action with its own Cloud Function.
    const [mfaResetLoading, setMfaResetLoading] = useState(false);
    const [mfaResetError, setMfaResetError] = useState(null);

    // Non-blocking warning from the CF (e.g. welcome email failed after success)
    const [submitWarning, setSubmitWarning] = useState('');

    // ── Disable/Enable state ─────────────────────────────────────────────────
    const [disableDryRunData, setDisableDryRunData] = useState(null);
    const [disableReason, setDisableReason] = useState('');
    const [isDryRunLoading, setIsDryRunLoading] = useState(false);

    // ── Account Type change state ────────────────────────────────────────────
    const [selectedAccountType, setSelectedAccountType] = useState(null);
    const [accountTypeReason, setAccountTypeReason] = useState('');
    const [accountTypeBlockedPatients, setAccountTypeBlockedPatients] = useState([]);
    const [successMessage, setSuccessMessage] = useState('Details Updated');

    // Holds the pending "sign yourself out" timer used after a SELF email change.
    // Changing your own sign-in email invalidates your current session, so we
    // deliberately delay the forced token refresh (which triggers the sign-out)
    // by a few seconds — otherwise the success screen flashes past before the
    // user can read that they are about to be logged out (tester feedback #4).
    const selfSignOutTimerRef = useRef(null);

    // Clear any pending self-sign-out timer if the component unmounts first
    // (e.g. the admin closes the modal), so we never touch Auth after teardown.
    useEffect(() => () => {
        if (selfSignOutTimerRef.current) {
            clearTimeout(selfSignOutTimerRef.current);
        }
    }, []);

    // Reset all form state every time the modal opens/closes or the target clinician changes.
    useEffect(() => {
        // If the modal closes or the admin switches to another staff member while a
        // self email-change sign-out timer is pending, cancel it. The modal stays
        // mounted in ProviderAccountsTable so unmount cleanup alone is not enough —
        // without this, an admin could be signed out unexpectedly after clicking Done
        // and opening another edit within the 4s window.
        if (!isOpen || !clinician) {
            if (selfSignOutTimerRef.current) {
                clearTimeout(selfSignOutTimerRef.current);
                selfSignOutTimerRef.current = null;
            }
        }

        if (isOpen && clinician) {
            setCurrentStep('summary');
            setEditData({
                title: clinician.title || '',
                firstName: clinician.firstName || '',
                lastName: clinician.lastName || '',
                role: clinician.customFields?.role || '',
                registrationBody: clinician.customFields?.registrationBody || '',
                registrationNumber: clinician.customFields?.registrationNumber || '',
                pathwaySpecialisms: getEffectiveSpecialisms(clinician),
                // Email identity fields — confirmEmail is always required so the
                // active and not-yet-signed-in edit flows share the same chrome.
                email: clinician.email || '',
                confirmEmail: '',
            });
            setEditCategory(null);
            setIsSubmitting(false);
            setSubmitError('');
            setSubmitWarning('');
            setDisableDryRunData(null);
            setDisableReason('');
            setIsDryRunLoading(false);
            setSelectedAccountType(null);
            setAccountTypeReason('');
            setAccountTypeBlockedPatients([]);
            setSuccessMessage('Details Updated');
        }
    }, [isOpen, clinician]);

    // ─── Helpers ──────────────────────────────────────────────────────────────

    const accountStatusLabel = getStaffAccountStatus(clinician);
    const accountIsActive = isStaffAccountActive(clinician);
    const emailEditSsoGated = isStaffEmailEditSsoGated(clinician);
    const callerCanEditEmail = canEditStaffEmail(userData, clinician);
    // Resend is broader than email edit: Support may resend for Org. Admins.
    const callerCanResendWelcome = canResendStaffWelcome(userData);
    const resendWelcomeEnabled = canResendStaffWelcomeForTarget(clinician);
    const welcomeFirstSent = resolveStaffWelcomeFirstSent(clinician);
    const staffAuthMode = (clinician?.authMode || 'hybrid').toLowerCase();

    // Build an object containing only the fields that differ from the original
    // clinician data. Returns null if nothing changed.
    // When editing email, only the email field is compared (confirmEmail is UI-only).
    const computeChangedFields = () => {
        if (!clinician) return null;

        if (editCategory === 'email') {
            const originalEmail = (clinician.email || '').trim().toLowerCase();
            const nextEmail = (editData.email || '').trim().toLowerCase();
            if (nextEmail && nextEmail !== originalEmail) {
                return { email: nextEmail };
            }
            return null;
        }

        const changes = {};
        const original = {
            title: clinician.title || '',
            firstName: clinician.firstName || '',
            lastName: clinician.lastName || '',
            role: clinician.customFields?.role || '',
            registrationBody: clinician.customFields?.registrationBody || '',
            registrationNumber: clinician.customFields?.registrationNumber || '',
        };

        for (const key of Object.keys(original)) {
            if (key === 'role') {
                // Send the trimmed job title. A blank role is not a valid
                // change: the save button stays disabled until one is entered.
                // Accounts that already have no role can still be edited
                // everywhere else, because this key is only compared here.
                const nextRole = normaliseProviderRole(editData.role || '');
                const previousRole = (original.role || '').trim();
                if (nextRole.ok && nextRole.role !== previousRole) {
                    changes.role = nextRole.role;
                }
                continue;
            }
            if (editData[key] !== original[key]) {
                changes[key] = editData[key];
            }
        }

        // Pathway specialisms are arrays, so we compare them element-by-element
        // rather than by reference. Two arrays with the same elements in any
        // order are considered equal (order does not matter for assignment logic).
        const originalSpecialisms = getEffectiveSpecialisms(clinician);
        const editedSpecialisms = editData.pathwaySpecialisms || [];
        const specialismsChanged =
            originalSpecialisms.length !== editedSpecialisms.length ||
            !originalSpecialisms.every(s => editedSpecialisms.includes(s));
        if (specialismsChanged) {
            changes.pathwaySpecialisms = editedSpecialisms;
        }

        return Object.keys(changes).length > 0 ? changes : null;
    };

    // Human-readable labels for each field, used in the confirmation diff view.
    const FIELD_LABELS = {
        title: 'Title',
        firstName: 'First Name',
        lastName: 'Last Name',
        role: 'Role',
        registrationBody: 'Registration Body',
        registrationNumber: 'Registration Number',
        pathwaySpecialisms: 'Pathways',
        email: 'Email Address',
    };

    const getOriginalValue = (key) => {
        // Defensive: confirm step can theoretically run before/after clinician flips;
        // closing the modal clears parent state while this component stays mounted for exit animation.
        if (!clinician) return '';
        if (key === 'pathwaySpecialisms') {
            // Return a human-readable comma-separated string of specialism labels
            // for display in the confirmation diff view.
            return getEffectiveSpecialisms(clinician)
                .map(s => PATHWAY_SPECIALISM_LABELS[s] || s)
                .join(', ');
        }
        if (['role', 'registrationBody', 'registrationNumber'].includes(key)) {
            return clinician.customFields?.[key] || '';
        }
        return clinician[key] || '';
    };

    // ─── Form validation ─────────────────────────────────────────────────────

    // Name fields: title, firstName, and lastName are required.
    const isNameFormValid = () => {
        return (
            editData.title?.trim() &&
            editData.firstName?.trim() &&
            editData.lastName?.trim()
        );
    };

    // Role is the job title on letters. It can be changed, but not cleared.
    // An account that already has no role can leave this screen without
    // setting one; the save button stays off until a real title is typed.
    const isRoleFormValid = () => normaliseProviderRole(editData.role || '').ok;

    // Registration: if a body is chosen (other than N/A), a number is recommended
    // but not strictly required. The form itself is always submittable.
    const isRegistrationFormValid = () => true;

    // Specialisms: at least one pathway must remain selected so the clinician
    // can always be assigned to at least one assessment type.
    const isSpecialismsFormValid = () => {
        return Array.isArray(editData.pathwaySpecialisms) && editData.pathwaySpecialisms.length > 0;
    };

    // Email: format valid, changed from current, and (for active accounts) typed twice.
    const isEmailFormReady = () => {
        // Always require the confirm field so active and not-yet-signed-in
        // accounts use the same form shape (only the guidance bullets differ).
        return isStaffEmailConfirmReady({
            currentEmail: clinician?.email,
            newEmail: editData.email,
            confirmEmail: editData.confirmEmail,
            requireConfirm: true,
        });
    };

    // ─── Submit handlers ─────────────────────────────────────────────────────

    const handleSubmitProfile = async () => {
        if (!clinician) return;
        const changes = computeChangedFields();
        if (!changes) return;

        setIsSubmitting(true);
        setSubmitError('');
        setSubmitWarning('');

        try {
            const updateFn = httpsCallable(functions, 'updateProviderUserDetails');
            await updateFn({
                targetClinicianId: clinician.clinicianId,
                updates: changes,
            });

            setCurrentStep('success');
            await refreshProviderAccountsData();
        } catch (err) {
            console.error('Error updating provider user details:', err);
            const message =
                err?.message || 'An unexpected error occurred. Please try again.';
            setSubmitError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendWelcome = async () => {
        if (!clinician || isSubmitting) return;

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const resendFn = httpsCallable(functions, 'resendProviderWelcomeEmail');
            const result = await resendFn({
                targetClinicianId: clinician.clinicianId,
            });
            const sentTo = result?.data?.email || clinician.email;

            trackEvent(ANALYTICS_EVENTS.RESEND_STAFF_WELCOME_SUCCESS, {
                authMode: staffAuthMode,
            });

            toast.success(`Welcome email resent to ${sentTo}`);
            setCurrentStep('summary');
            // Refresh so welcomeEmailFirstSentAt appears on later confirm opens
            await refreshProviderAccountsData();
        } catch (err) {
            console.error('Error resending staff welcome email:', err);
            trackEvent(ANALYTICS_EVENTS.RESEND_STAFF_WELCOME_FAILED, {
                authMode: staffAuthMode,
                errorCode: err?.code || 'unknown',
            });
            setSubmitError('Unable to send welcome email. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitEmail = async () => {
        if (!clinician) return;
        const changes = computeChangedFields();
        if (!changes?.email) return;

        setIsSubmitting(true);
        setSubmitError('');
        setSubmitWarning('');

        try {
            const updateFn = httpsCallable(functions, 'updateProviderUserEmail');
            const result = await updateFn({
                targetClinicianId: clinician.clinicianId,
                newEmail: changes.email,
            });
            const data = result?.data || {};

            if (data.warning) {
                setSubmitWarning(data.warning);
            }

            trackEvent(ANALYTICS_EVENTS.EDIT_STAFF_EMAIL_SUCCESS, {
                accountStatus: data.accountStatus || (accountIsActive ? 'active' : 'not_yet_signed_in'),
            });

            setCurrentStep('success');

            // Refresh staff table; if the caller edited their own email, also
            // refresh userData + Auth so settings/header don't show the old address.
            await refreshProviderAccountsData();
            if (userData?.clinicianId === clinician.clinicianId) {
                // Self email change: the new address is now the sign-in identity and
                // the old session token is no longer valid, so forcing a token
                // refresh signs the user out. We delay that by a few seconds so the
                // success screen ("you will be signed out...") is readable first —
                // tester feedback #4 was that the logout happened too fast to read.
                // A ref-tracked timer lets us cancel it cleanly if the modal unmounts.
                const SELF_SIGNOUT_DELAY_MS = 4000;
                if (selfSignOutTimerRef.current) {
                    clearTimeout(selfSignOutTimerRef.current);
                }
                selfSignOutTimerRef.current = setTimeout(async () => {
                    try {
                        if (auth.currentUser) {
                            await auth.currentUser.reload();
                            await auth.currentUser.getIdToken(true);
                        }
                    } catch (reloadErr) {
                        console.warn('Auth reload after self email change failed:', reloadErr);
                    }
                    if (typeof refreshUserDataOnly === 'function') {
                        await refreshUserDataOnly();
                    }
                }, SELF_SIGNOUT_DELAY_MS);
            }
        } catch (err) {
            console.error('Error updating provider user email:', err);
            const code = err?.code || '';
            const isDuplicate =
                code === 'functions/already-exists' ||
                /already associated with another account/i.test(err?.message || '');

            if (isDuplicate) {
                trackEvent(ANALYTICS_EVENTS.EDIT_STAFF_EMAIL_DUPLICATE_BLOCKED);
                setSubmitError(
                    'This email address is already associated with another account. Please check and try again.'
                );
            } else {
                trackEvent(ANALYTICS_EVENTS.EDIT_STAFF_EMAIL_FAILED, {
                    errorCode: code || 'unknown',
                });
                setSubmitError(
                    err?.message?.includes('Microsoft') || err?.message?.includes('sign-in')
                        ? err.message
                        : 'Unable to update the email address. Please try again.'
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = () => {
        if (editCategory === 'email') {
            return handleSubmitEmail();
        }
        return handleSubmitProfile();
    };

    // ─── Navigation helpers ──────────────────────────────────────────────────

    const goToEditName = () => {
        setEditCategory('name');
        setCurrentStep('edit-name');
    };

    const goToEditRole = () => {
        setEditCategory('role');
        setCurrentStep('edit-role');
    };

    const goToEditRegistration = () => {
        setEditCategory('registration');
        setCurrentStep('edit-registration');
    };

    const goToEditEmail = () => {
        trackEvent(ANALYTICS_EVENTS.EDIT_STAFF_EMAIL_OPENED, {
            accountStatus: accountIsActive ? 'active' : 'not_yet_signed_in',
        });
        setEditCategory('email');
        // Leave "New email address" empty — pre-filling the current address
        // made it too easy to "confirm" without intending a real change, and
        // testers found it confusing for both active and not-yet-signed-in
        // accounts. The current address is already shown on the summary row.
        setEditData(prev => ({
            ...prev,
            email: '',
            confirmEmail: '',
        }));
        setSubmitError('');
        setCurrentStep('edit-email');
    };

    const goToConfirmResend = () => {
        trackEvent(ANALYTICS_EVENTS.RESEND_STAFF_WELCOME_CLICKED, {
            authMode: staffAuthMode,
        });
        setSubmitError('');
        setCurrentStep('confirm-resend');
    };

    const goToConfirm = () => {
        const changes = computeChangedFields();
        if (changes) {
            setCurrentStep('confirm');
        }
    };

    const goBackToSummary = () => {
        if (editCategory === 'email' && currentStep !== 'success') {
            trackEvent(ANALYTICS_EVENTS.EDIT_STAFF_EMAIL_CANCELLED);
        }
        setCurrentStep('summary');
        setSubmitError('');
    };

    const goToEditSpecialisms = () => {
        setEditCategory('specialisms');
        setCurrentStep('edit-specialisms');
    };

    // ── Disable/Enable handlers ──────────────────────────────────────────────

    // Whether the caller is allowed to perform the disable/enable action on
    // this particular target.  Mirrors the Cloud Function's authorization.
    const isSelf = user?.uid === clinician?.clinicianId;
    const callerIsOrgAdmin = isOrgAdmin();
    const callerIsSupport = isSupportUser();
    const targetIsOrgAdmin = clinician?.userType === 'Org. Admin';
    const targetIsDisabled = clinician?.accountDisabled === true;

    // Determines whether the disable/enable action card should be greyed out.
    // Enable remains available when the account is disabled (that's how you
    // re-enable) — only self / Support→Org Admin role gates block it.
    const isDisableActionBlocked = isSelf || (callerIsSupport && targetIsOrgAdmin);

    // Tooltip text explaining why the disable/enable action is unavailable.
    // Verb must match the row label: when the target is already disabled the
    // card says "Enable Account", so the tooltip must say "cannot enable..."
    // (not "cannot disable...") — Support→disabled-Org-Admin was a tester miss.
    const disableEnableVerb = targetIsDisabled ? 'enable' : 'disable';
    const disableBlockedTooltip = isSelf
        ? `You cannot ${disableEnableVerb} your own account.`
        : (callerIsSupport && targetIsOrgAdmin)
            ? `Support users cannot ${disableEnableVerb} Organisation Administrator accounts.`
            : '';

    // Shared copy for every Account Management action that is blocked solely
    // because the target is disabled (Edit Email / Resend Welcome / Edit Account
    // Type). Tester feedback: when an account is disabled these rows previously
    // showed unrelated reasons (role gates, "already signed in"), which was
    // confusing — the actionable next step is always "re-enable first".
    const disabledAccountTooltip = (actionPhrase) => (
        `This user's account is disabled. Re-enable it first to ${actionPhrase}.`
    );

    // Edit Email — disabled-account gate takes precedence over role/SSO so a
    // disabled Org Admin shows the same "re-enable first" tooltip as every
    // other blocked row (rather than the Support→Org Admin role message).
    const emailDisabledByAccount = targetIsDisabled;
    const emailRoleBlocked = !callerCanEditEmail;
    const isEmailActionBlocked =
        emailDisabledByAccount || emailRoleBlocked || emailEditSsoGated;
    const emailBlockedTooltip = emailDisabledByAccount
        ? disabledAccountTooltip('edit the email address')
        : emailRoleBlocked
            ? 'Support users cannot change Organisation Administrator email addresses.'
            : 'Email addresses for Microsoft sign-in organisations cannot be changed here. Please contact engineering support.';

    // Resend Welcome — same disabled-account precedence. An already-signed-in
    // user on a disabled account still gets the re-enable message, not the
    // "only before first sign-in" copy.
    const resendDisabledByAccount = targetIsDisabled;
    const isResendActionBlocked = resendDisabledByAccount || !resendWelcomeEnabled;
    const resendBlockedTooltip = resendDisabledByAccount
        ? disabledAccountTooltip('resend the welcome email')
        : 'A welcome email can only be resent before the staff member has signed in and set up their password.';

    // MFA Reset — blocked when: target is SSO-managed, target is disabled,
    // caller is targeting themselves, or Support targeting Org Admin.
    // Auth is the source of truth for MFA status (not the Firestore
    // isMFASetUp flag, which can drift). The UI no longer gates on
    // isMFASetUp — the backend checks Auth directly and returns
    // alreadyReset if no factors are found.
    const isSsoUser = staffAuthMode === 'with_microsoft_sso';
    const isMfaResetBlocked =
        isSsoUser
        || targetIsDisabled
        || isSelf
        || (callerIsSupport && targetIsOrgAdmin);
    const mfaResetBlockedTooltip =
        targetIsDisabled
            ? disabledAccountTooltip('reset two-factor authentication')
            : isSelf
                ? 'You cannot reset your own two-factor authentication. Use the email revert link or contact another administrator.'
                : (callerIsSupport && targetIsOrgAdmin)
                    ? 'Support users cannot manage Organisation Administrator accounts.'
                    : isSsoUser
                        ? 'Two-factor authentication for Microsoft sign-in users is managed in Microsoft Entra, not in this platform.'
                        : '';

    // Human-readable label for the MFA method Firestore has recorded.
    // Shown as informational context on the action card — the backend
    // checks Auth directly, so this is a "last recorded" hint, not a gate.
    const mfaRecordedLabel = clinician?.mfaMethod === 'totp'
        ? 'Recorded method: Authenticator app'
        : clinician?.mfaMethod === 'phone'
            ? 'Recorded method: SMS'
            : clinician?.isMFASetUp === true
                ? 'Recorded method: Set up (type not recorded)'
                : 'No method recorded';

    // Determines whether the account type action card should be greyed out.
    const isAccountTypeActionBlocked =
        (!callerIsOrgAdmin) || targetIsDisabled;
    const accountTypeBlockedTooltip = targetIsDisabled
        ? disabledAccountTooltip('change the account type')
        : (!callerIsOrgAdmin)
            ? 'Only an Org. Admin or Org. Admin (Clinical) can change a user\'s account type. Please reach out to one of them if you need this changed.'
            : '';

    const goToDisableAccount = async () => {
        trackEvent(ANALYTICS_EVENTS.DISABLE_STAFF_ACCOUNT_OPENED, {
            targetUserType: clinician?.userType,
        });
        setSubmitError('');
        setDisableReason('');
        setIsDryRunLoading(true);
        setCurrentStep('disable-account');

        try {
            const disableFn = httpsCallable(functions, 'disableProviderUser');
            const result = await disableFn({
                targetClinicianId: clinician.clinicianId,
                dryRun: true,
            });
            setDisableDryRunData(result.data);
        } catch (err) {
            console.error('dryRun failed:', err);
            setSubmitError(err?.message || 'Failed to load assigned patient data.');
        } finally {
            setIsDryRunLoading(false);
        }
    };

    const goToEnableAccount = () => {
        trackEvent(ANALYTICS_EVENTS.ENABLE_STAFF_ACCOUNT_OPENED, {
            targetUserType: clinician?.userType,
        });
        setSubmitError('');
        setCurrentStep('enable-account');
    };

    const handleDisableSubmit = async () => {
        if (!clinician) return;
        setIsSubmitting(true);
        setSubmitError('');

        try {
            const disableFn = httpsCallable(functions, 'disableProviderUser');
            await disableFn({
                targetClinicianId: clinician.clinicianId,
                dryRun: false,
                reason: disableReason,
            });
            trackEvent(ANALYTICS_EVENTS.DISABLE_STAFF_ACCOUNT_SUCCESS, {
                targetUserType: clinician?.userType,
                hadReason: Boolean(disableReason?.trim()),
            });
            setSuccessMessage('Account Disabled');
            setCurrentStep('success');
            await refreshProviderAccountsData();
        } catch (err) {
            console.error('Disable failed:', err);
            trackEvent(ANALYTICS_EVENTS.DISABLE_STAFF_ACCOUNT_FAILED, {
                targetUserType: clinician?.userType,
                errorCode: err?.code || 'unknown',
            });
            setSubmitError(err?.message || 'An unexpected error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEnableSubmit = async () => {
        if (!clinician) return;
        setIsSubmitting(true);
        setSubmitError('');

        try {
            const enableFn = httpsCallable(functions, 'enableProviderUser');
            await enableFn({ targetClinicianId: clinician.clinicianId });
            trackEvent(ANALYTICS_EVENTS.ENABLE_STAFF_ACCOUNT_SUCCESS, {
                targetUserType: clinician?.userType,
            });
            setSuccessMessage('Account Enabled');
            setCurrentStep('success');
            await refreshProviderAccountsData();
        } catch (err) {
            console.error('Enable failed:', err);
            trackEvent(ANALYTICS_EVENTS.ENABLE_STAFF_ACCOUNT_FAILED, {
                targetUserType: clinician?.userType,
                errorCode: err?.code || 'unknown',
            });
            setSubmitError(err?.message || 'An unexpected error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Account Type change handlers ─────────────────────────────────────────

    const goToEditAccountType = () => {
        trackEvent(ANALYTICS_EVENTS.CHANGE_STAFF_ACCOUNT_TYPE_OPENED, {
            currentUserType: clinician?.userType,
        });
        setSubmitError('');
        setAccountTypeReason('');
        setSelectedAccountType(null);
        setAccountTypeBlockedPatients([]);
        setCurrentStep('edit-account-type');
    };

    // Returns the ACCOUNT_TYPE_OPTIONS formatted for react-select, excluding
    // the target's current type so they cannot "change" to what they already are.
    const getAccountTypeDropdownOptions = () => {
        const currentLabel = getUiUserType(clinician);
        return ACCOUNT_TYPE_OPTIONS
            .filter(opt => opt.label !== currentLabel)
            .map(opt => ({ value: opt, label: opt.label }));
    };

    const handleAccountTypeReview = async () => {
        if (!selectedAccountType || !clinician) return;
        setIsSubmitting(true);
        setSubmitError('');

        try {
            const changeFn = httpsCallable(functions, 'changeUserAccountType');
            const result = await changeFn({
                targetClinicianId: clinician.clinicianId,
                newUserType: selectedAccountType.userType,
                newIsAssignableClinician: selectedAccountType.isAssignableClinician,
                reason: accountTypeReason,
            });

            // If the CF returned successfully, proceed to success. The CF may
            // attach a non-blocking `warning` (e.g. the user's live session
            // permissions could not be refreshed) — surface it on the success
            // screen so the admin knows to retry or ask the user to re-login.
            const changeWarning = result?.data?.warning;
            if (changeWarning) {
                setSubmitWarning(changeWarning);
            }
            trackEvent(ANALYTICS_EVENTS.CHANGE_STAFF_ACCOUNT_TYPE_SUCCESS, {
                currentUserType: clinician?.userType,
                newUserType: selectedAccountType?.userType,
                claimsUpdated: result?.data?.claimsUpdated !== false,
            });
            setSuccessMessage('Account Type Updated');
            setCurrentStep('success');
            await refreshProviderAccountsData();

            // If an admin changed their OWN account type (e.g. a clinical Org.
            // Admin flipping their own assignable flag, or a permitted
            // self-demotion), immediately refresh their session so elevated UI
            // access drops right away instead of lingering on stale claims/
            // userData until the token naturally expires. This mirrors the
            // self-email-change path: reload Auth, force a new ID token (picks up
            // the new custom claims), then re-fetch userData (drives nav/role UI).
            if (isSelf) {
                try {
                    if (auth.currentUser) {
                        await auth.currentUser.reload();
                        await auth.currentUser.getIdToken(true);
                    }
                } catch (reloadErr) {
                    console.warn('Auth reload after self account-type change failed:', reloadErr);
                }
                if (typeof refreshUserDataOnly === 'function') {
                    await refreshUserDataOnly();
                }
            }
        } catch (err) {
            console.error('Account type change failed:', err);

            // FAILED_PRECONDITION errors carry structured details for the UI.
            const code = err?.code;
            const details = err?.details;

            if (code === 'functions/failed-precondition' && details?.patients) {
                // Gated transition: clinical -> non-clinical with assigned patients.
                trackEvent(ANALYTICS_EVENTS.CHANGE_STAFF_ACCOUNT_TYPE_BLOCKED, {
                    currentUserType: clinician?.userType,
                    reason: 'assigned_patients',
                    patientCount: details.patients.length,
                });
                setAccountTypeBlockedPatients(details.patients);
                setCurrentStep('account-type-blocked');
            } else if (code === 'functions/failed-precondition' && err?.message?.includes('last Organisation Administrator')) {
                // Last-admin protection: keep the admin on the account-type
                // screen and surface the server's message inline (mirrors how
                // the pre-emptive last-admin warning is shown above the form).
                trackEvent(ANALYTICS_EVENTS.CHANGE_STAFF_ACCOUNT_TYPE_BLOCKED, {
                    currentUserType: clinician?.userType,
                    reason: 'last_admin',
                });
                setSubmitError(
                    err?.message
                    || 'Cannot remove the last Organisation Administrator. Assign another '
                    + 'Org. Admin before changing this account type.'
                );
            } else {
                trackEvent(ANALYTICS_EVENTS.CHANGE_STAFF_ACCOUNT_TYPE_FAILED, {
                    currentUserType: clinician?.userType,
                    errorCode: code || 'unknown',
                });
                setSubmitError(err?.message || 'An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Whether this clinician's user type supports pathway editing.
    // Only assignable clinical users (Clinician and Clinical Org. Admin) hold
    // pathway assignments. Support users and non-clinical Org. Admins
    // (isAssignableClinician === false) cannot be assigned to patients, so
    // the pathway field is irrelevant for them.
    const isSpecialismEligible =
        (clinician?.userType === 'Clinician' || clinician?.userType === 'Org. Admin')
        && clinician?.isAssignableClinician !== false;

    // ─── Render: Summary screen ──────────────────────────────────────────────

    const renderSummary = () => (
        <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900 pr-6">
                Edit Provider Details
            </h3>

            {/* Current details card */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <UserIcon className="h-4 w-4 text-violet-700" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-900">
                            {[clinician?.title, clinician?.firstName, clinician?.lastName].filter(Boolean).join(' ') || '—'}
                        </p>
                        <p className="text-xs text-gray-500">{clinician?.email ?? '—'}</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div>
                        <span className="text-gray-500">Role:</span>{' '}
                        <span className="text-gray-700 font-medium">
                            {clinician?.customFields?.role || '—'}
                        </span>
                    </div>
                    <div>
                        <span className="text-gray-500">Registration:</span>{' '}
                        <span className="text-gray-700 font-medium">
                            {clinician?.customFields?.registrationBody && clinician?.customFields?.registrationNumber
                                ? `${clinician.customFields.registrationBody}: ${clinician.customFields.registrationNumber}`
                                : '—'}
                        </span>
                    </div>
                    <div className="col-span-2">
                        <span className="text-gray-500">Account status:</span>{' '}
                        <span className="text-gray-700 font-medium">{accountStatusLabel}</span>
                    </div>
                    {isSpecialismEligible && (
                        <div className="col-span-2">
                            <span className="text-gray-500">Pathways:</span>{' '}
                            <span className="text-gray-700 font-medium">
                                {getEffectiveSpecialisms(clinician)
                                    .map(s => PATHWAY_SPECIALISM_LABELS[s] || s)
                                    .join(', ') || '—'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Profile-detail edit cards. Account-level actions (email, resend,
                disable/enable, change type) live in the Account Management
                section below so all access/identity operations are grouped. */}
            <div className="bg-white border border-gray-200 rounded-md p-3">
                <h4 className="text-xs font-medium text-gray-900 mb-2">Edit Details</h4>
                <div className="space-y-2">
                    {/* Edit Name & Title */}
                    <button
                        onClick={goToEditName}
                        className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors"
                    >
                        <div className="flex items-center space-x-2">
                            <UserIcon className="h-4 w-4 text-violet-600" />
                            <div>
                                <p className="text-xs font-semibold text-gray-900">Edit Name & Title</p>
                                <p className="text-xs text-gray-500">Update title, first name, or last name</p>
                            </div>
                        </div>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                    </button>

                    {/* Edit Role */}
                    <button
                        onClick={goToEditRole}
                        className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors"
                    >
                        <div className="flex items-center space-x-2">
                            <ClipboardListIcon className="h-4 w-4 text-violet-600" />
                            <div>
                                <p className="text-xs font-semibold text-gray-900">Edit Role</p>
                                <p className="text-xs text-gray-500">Update the role displayed in documents</p>
                            </div>
                        </div>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                    </button>

                    {/* Edit Registration */}
                    <button
                        onClick={goToEditRegistration}
                        className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors"
                    >
                        <div className="flex items-center space-x-2">
                            <IdentificationIcon className="h-4 w-4 text-violet-600" />
                            <div>
                                <p className="text-xs font-semibold text-gray-900">Edit Registration</p>
                                <p className="text-xs text-gray-500">Update registration body and number</p>
                            </div>
                        </div>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                    </button>

                    {/* Edit Pathways -- only for assignable clinical user types
                        (Clinician, Clinical Org. Admin). Non-clinical Org. Admins
                        and Support users are not assignable to patients. */}
                    {isSpecialismEligible && (
                        <button
                            onClick={goToEditSpecialisms}
                            className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors"
                        >
                            <div className="flex items-center space-x-2">
                                <ClipboardListIcon className="h-4 w-4 text-violet-600" />
                                <div>
                                    <p className="text-xs font-semibold text-gray-900">Edit Pathways</p>
                                    <p className="text-xs text-gray-500">Update which assessment pathways this user handles</p>
                                </div>
                            </div>
                            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                        </button>
                    )}

                </div>
            </div>

            {/* Account Management section -- all account access/identity actions
                (edit email, resend welcome, disable/enable, change type) grouped
                together. Visible to Org. Admin and Support (the same population
                allowed to edit email / resend welcome). Each card keeps its own
                per-action gating. */}
            {(callerIsOrgAdmin || callerIsSupport) && (
                <div className="bg-white border border-gray-200 rounded-md p-3">
                    <h4 className="text-xs font-medium text-gray-900 mb-2">Account Management</h4>
                    <div className="space-y-2">
                        {/* Edit Email Address — always shown in Account Management for
                            Org. Admin / Support. Greyed out (with InfoTooltip) when
                            blocked: target disabled, Support→Org Admin, or SSO. */}
                        {(() => {
                            const emailDisabled = isEmailActionBlocked;
                            const sharedClasses = `w-full flex items-center justify-between p-2 text-left border rounded-md transition-colors ${
                                emailDisabled
                                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                    : 'border-gray-200 hover:bg-violet-50 hover:border-violet-200'
                            }`;
                            const inner = (
                                <>
                                    <div className="flex items-center space-x-2 min-w-0">
                                        <MailIcon className={`h-4 w-4 flex-shrink-0 ${emailDisabled ? 'text-gray-400' : 'text-violet-600'}`} />
                                        <div className="min-w-0">
                                            <p className={`text-xs font-semibold ${emailDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
                                                Edit Email Address
                                            </p>
                                            <p className={`text-xs break-words ${emailDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {clinician?.email
                                                    ? `Update login email from ${clinician.email}`
                                                    : 'No email set'}
                                            </p>
                                        </div>
                                    </div>
                                    {emailDisabled ? (
                                        <InfoTooltip
                                            content={emailBlockedTooltip}
                                            side="left"
                                            maxWidth="220px"
                                        />
                                    ) : (
                                        <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    )}
                                </>
                            );
                            return emailDisabled ? (
                                <div className={sharedClasses}>{inner}</div>
                            ) : (
                                <button type="button" onClick={goToEditEmail} className={sharedClasses}>
                                    {inner}
                                </button>
                            );
                        })()}

                        {/* Resend Welcome Email — Org. Admin / Support for any same-org
                            staff role. Greyed after first sign-in, or while the
                            account is disabled (re-enable first). */}
                        {callerCanResendWelcome && (() => {
                            const resendDisabled = isResendActionBlocked;
                            const sharedClasses = `w-full flex items-center justify-between p-2 text-left border rounded-md transition-colors ${
                                resendDisabled
                                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                    : 'border-gray-200 hover:bg-violet-50 hover:border-violet-200'
                            }`;
                            const inner = (
                                <>
                                    <div className="flex items-center space-x-2 min-w-0">
                                        <MailIcon className={`h-4 w-4 flex-shrink-0 ${resendDisabled ? 'text-gray-400' : 'text-violet-600'}`} />
                                        <div className="min-w-0">
                                            <p className={`text-xs font-semibold ${resendDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
                                                Resend Welcome Email
                                            </p>
                                            <p className={`text-xs break-words ${resendDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {resendDisabledByAccount
                                                    ? "Unavailable while this user's account is disabled"
                                                    : resendDisabled
                                                        ? 'Only available before the staff member has signed in'
                                                        : (clinician?.email
                                                            ? `Send setup email to ${clinician.email}`
                                                            : 'No email set')}
                                            </p>
                                        </div>
                                    </div>
                                    {resendDisabled ? (
                                        <InfoTooltip
                                            content={resendBlockedTooltip}
                                            side="left"
                                            maxWidth="220px"
                                        />
                                    ) : (
                                        <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    )}
                                </>
                            );
                            return resendDisabled ? (
                                <div className={sharedClasses}>{inner}</div>
                            ) : (
                                <button type="button" onClick={goToConfirmResend} className={sharedClasses}>
                                    {inner}
                                </button>
                            );
                        })()}

                        {/* Disable / Enable Account — when blocked (self / Support→Org Admin),
                            use InfoTooltip like Resend Welcome so long explanations don't
                            overflow the modal via react-tooltip hover balloons. */}
                        {(() => {
                            const disableDisabled = isDisableActionBlocked;
                            const sharedClasses = `w-full flex items-center justify-between p-2 text-left border rounded-md transition-colors ${
                                disableDisabled
                                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                    : targetIsDisabled
                                        ? 'border-green-200 hover:bg-green-50 hover:border-green-300'
                                        : 'border-red-200 hover:bg-red-50 hover:border-red-300'
                            }`;
                            const inner = (
                                <>
                                    <div className="flex items-center space-x-2 min-w-0">
                                        <BanIcon className={`h-4 w-4 flex-shrink-0 ${
                                            disableDisabled ? 'text-gray-400'
                                                : targetIsDisabled ? 'text-green-600' : 'text-red-600'
                                        }`} />
                                        <div className="min-w-0">
                                            <p className={`text-xs font-semibold ${
                                                disableDisabled ? 'text-gray-400' : 'text-gray-900'
                                            }`}>
                                                {targetIsDisabled ? 'Enable Account' : 'Disable Account'}
                                            </p>
                                            <p className={`text-xs break-words ${
                                                disableDisabled ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                                {targetIsDisabled
                                                    ? "Re-enable this user's access"
                                                    : 'Prevent this user from signing in'}
                                            </p>
                                        </div>
                                    </div>
                                    {disableDisabled ? (
                                        <InfoTooltip
                                            content={disableBlockedTooltip}
                                            side="left"
                                            maxWidth="220px"
                                        />
                                    ) : (
                                        <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    )}
                                </>
                            );
                            return disableDisabled ? (
                                <div className={sharedClasses}>{inner}</div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={targetIsDisabled ? goToEnableAccount : goToDisableAccount}
                                    className={sharedClasses}
                                >
                                    {inner}
                                </button>
                            );
                        })()}

                        {/* Change Account Type — same InfoTooltip pattern when blocked
                            (non-admin caller or target disabled) so long copy stays
                            inside the modal instead of forcing horizontal scroll. */}
                        {(() => {
                            const typeDisabled = isAccountTypeActionBlocked;
                            const sharedClasses = `w-full flex items-center justify-between p-2 text-left border rounded-md transition-colors ${
                                typeDisabled
                                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                    : 'border-gray-200 hover:bg-violet-50 hover:border-violet-200'
                            }`;
                            const inner = (
                                <>
                                    <div className="flex items-center space-x-2 min-w-0">
                                        <SwitchHorizontalIcon className={`h-4 w-4 flex-shrink-0 ${
                                            typeDisabled ? 'text-gray-400' : 'text-violet-600'
                                        }`} />
                                        <div className="min-w-0">
                                            <p className={`text-xs font-semibold ${
                                                typeDisabled ? 'text-gray-400' : 'text-gray-900'
                                            }`}>
                                                Edit Account Type
                                            </p>
                                            <p className={`text-xs break-words ${
                                                typeDisabled ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                                Change this user's account type
                                            </p>
                                        </div>
                                    </div>
                                    {typeDisabled ? (
                                        <InfoTooltip
                                            content={accountTypeBlockedTooltip}
                                            side="left"
                                            maxWidth="220px"
                                        />
                                    ) : (
                                        <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    )}
                                </>
                            );
                            return typeDisabled ? (
                                <div className={sharedClasses}>{inner}</div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={goToEditAccountType}
                                    className={sharedClasses}
                                >
                                    {inner}
                                </button>
                            );
                        })()}

                        {/* Reset two-factor authentication — disabled with
                            tooltip when blocked by SSO, disabled account,
                            self-targeting, or Support targeting Org Admin.
                            Auth is the source of truth; isMFASetUp is not
                            a gate — the backend checks Auth directly. */}
                        {(() => {
                            const mfaDisabled = isMfaResetBlocked;
                            const sharedClasses = `w-full flex items-center justify-between p-2 text-left border rounded-md transition-colors ${
                                mfaDisabled
                                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                    : 'border-gray-200 hover:bg-violet-50 hover:border-violet-200'
                            }`;
                            const inner = (
                                <>
                                    <div className="flex items-center space-x-2 min-w-0">
                                        <ShieldExclamationIcon className={`h-4 w-4 flex-shrink-0 ${
                                            mfaDisabled ? 'text-gray-400' : 'text-violet-600'
                                        }`} />
                                        <div className="min-w-0">
                                            <p className={`text-xs font-semibold ${
                                                mfaDisabled ? 'text-gray-400' : 'text-gray-900'
                                            }`}>
                                                Reset two-factor authentication
                                            </p>
                                            <p className={`text-xs break-words ${
                                                mfaDisabled ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                                {mfaDisabled
                                                    ? 'Reset is not available for this account'
                                                    : mfaRecordedLabel}
                                            </p>
                                        </div>
                                    </div>
                                    {mfaDisabled ? (
                                        <InfoTooltip
                                            content={mfaResetBlockedTooltip}
                                            side="left"
                                            maxWidth="220px"
                                        />
                                    ) : (
                                        <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    )}
                                </>
                            );
                            return mfaDisabled ? (
                                <div className={sharedClasses}>{inner}</div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep('reset-mfa')}
                                    className={sharedClasses}
                                >
                                    {inner}
                                </button>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Info banner about propagation */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex items-start space-x-2">
                    <ExclamationIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-800">
                        Changes to name, role, or registration details will be propagated to all patient records where this user is assigned, but it will not update point-in-time records (e.g. audit logs) or already generated or approved documents such as Assessment Reports or Patient Reports.
                    </p>
                </div>
            </div>
        </div>
    );

    // ─── Render: Edit Name & Title form ──────────────────────────────────────

    const renderEditName = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Edit Name & Title</h3>

            {/* Warning about widespread update */}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex items-start space-x-2">
                    <ExclamationIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                        Changing the name will update this user's display name across
                        the platform, including all patient records they are assigned to.
                    </p>
                </div>
            </div>

            <div className="space-y-3">
                {/* Title dropdown */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                    <Select
                        {...DIALOG_SELECT_PORTAL_PROPS}
                        options={TITLE_OPTIONS}
                        value={TITLE_OPTIONS.find(o => o.value === editData.title) || null}
                        onChange={(selected) => setEditData(prev => ({ ...prev, title: selected?.value || '' }))}
                        placeholder="Select title..."
                        className="text-sm"
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({ ...base, minHeight: '36px', borderColor: '#d1d5db', '&:hover': { borderColor: '#7c3aed' } }),
                            option: (base, state) => ({ ...base, fontSize: '0.875rem', backgroundColor: state.isSelected ? '#7c3aed' : state.isFocused ? '#ede9fe' : 'white' }),
                        }}
                    />
                </div>

                {/* First Name */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                    <input
                        type="text"
                        value={editData.firstName}
                        onChange={(e) => setEditData(prev => ({ ...prev, firstName: sanitizeNameInput(e.target.value) }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        placeholder="Enter first name"
                    />
                </div>

                {/* Last Name */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                        type="text"
                        value={editData.lastName}
                        onChange={(e) => setEditData(prev => ({ ...prev, lastName: sanitizeNameInput(e.target.value) }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        placeholder="Enter last name"
                    />
                </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={goToConfirm}
                    disabled={!isNameFormValid() || !computeChangedFields()}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                        isNameFormValid() && computeChangedFields()
                            ? 'bg-violet-600 hover:bg-violet-700'
                            : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                >
                    Review Changes
                </button>
            </div>
        </div>
    );

    // ─── Render: Edit Role form ──────────────────────────────────────────────

    const renderEditRole = () => {
        const roleCheck = normaliseProviderRole(editData.role || '');
        const showRoleError = Boolean((editData.role || '').trim() && !roleCheck.ok);
        return (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Edit Role</h3>

            <div>
                <label htmlFor="edit-provider-role" className="block text-xs font-medium text-gray-700 mb-1">
                    Role <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-1">
                    This is the job title that appears on letters and reports.
                </p>
                <input
                    id="edit-provider-role"
                    type="text"
                    value={editData.role}
                    maxLength={120}
                    aria-required="true"
                    aria-invalid={showRoleError ? 'true' : undefined}
                    onChange={(e) => setEditData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    placeholder="Enter role"
                />
                {showRoleError && (
                    <p className="text-xs text-red-600 mt-1" role="alert">{roleCheck.message}</p>
                )}
            </div>

            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={goToConfirm}
                    disabled={!isRoleFormValid() || !computeChangedFields()}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                        isRoleFormValid() && computeChangedFields()
                            ? 'bg-violet-600 hover:bg-violet-700'
                            : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                >
                    Review Changes
                </button>
            </div>
        </div>
        );
    };

    // ─── Render: Edit Registration form ──────────────────────────────────────

    const renderEditRegistration = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Edit Registration</h3>

            <div className="space-y-3">
                {/* Registration Body dropdown */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Registration Body</label>
                    <Select
                        {...DIALOG_SELECT_PORTAL_PROPS}
                        options={REGISTRATION_BODY_OPTIONS}
                        value={REGISTRATION_BODY_OPTIONS.find(o => o.value === editData.registrationBody) || null}
                        onChange={(selected) => setEditData(prev => ({ ...prev, registrationBody: selected?.value || '' }))}
                        placeholder="Select registration body..."
                        className="text-sm"
                        isClearable
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({ ...base, minHeight: '36px', borderColor: '#d1d5db', '&:hover': { borderColor: '#7c3aed' } }),
                            option: (base, state) => ({ ...base, fontSize: '0.875rem', backgroundColor: state.isSelected ? '#7c3aed' : state.isFocused ? '#ede9fe' : 'white' }),
                        }}
                    />
                </div>

                {/* Registration Number */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Registration Number</label>
                    <input
                        type="text"
                        value={editData.registrationNumber}
                        onChange={(e) => setEditData(prev => ({ ...prev, registrationNumber: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        placeholder="Enter registration number"
                    />
                </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={goToConfirm}
                    disabled={!isRegistrationFormValid() || !computeChangedFields()}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                        isRegistrationFormValid() && computeChangedFields()
                            ? 'bg-violet-600 hover:bg-violet-700'
                            : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                >
                    Review Changes
                </button>
            </div>
        </div>
    );

    // ─── Render: Edit Email form ─────────────────────────────────────────────
    // Active and not-yet-signed-in accounts share the same chrome (info panel +
    // new email + confirm email). Only the guidance bullets differ, because the
    // backend side-effects differ (welcome resend vs Auth login email update).
    //
    // Self-edit uses 2nd-person copy ("you" / "your"); editing another staff
    // member keeps 3rd-person. The confirm step adds one extra bullet about
    // the next login — for self, that replaces the old "please inform the
    // staff member" wording.

    const renderActiveEmailWarningBullets = ({ includeNextLoginNote = false } = {}) => {
        if (isSelf) {
            return (
                <>
                    <li>You have already created your account.</li>
                    <li>Changing your email address will change the email you use to sign in.</li>
                    <li>
                        Where supported by the authentication provider, your existing password
                        and two-factor authentication will remain unchanged.
                    </li>
                    {includeNextLoginNote && (
                        <li>
                            <strong>After you confirm, you will be signed out</strong> and will
                            need to sign in again with your new email address.
                        </li>
                    )}
                </>
            );
        }
        return (
            <>
                <li>This staff member has already created their account.</li>
                <li>Changing their email address will change the email they use to sign in.</li>
                <li>
                    Where supported by the authentication provider, their existing password
                    and two-factor authentication will remain unchanged.
                </li>
                {includeNextLoginNote && (
                    <li>
                        Please ensure you have informed the staff member that their sign-in
                        email address is changing, as they will need to use the new email
                        address the next time they sign in.
                    </li>
                )}
            </>
        );
    };

    const renderPendingEmailWarningBullets = () => {
        if (isSelf) {
            return (
                <>
                    <li>A <strong>new welcome email</strong> will be sent to the updated address.</li>
                    <li>You must use the <strong>new email</strong> to create your account.</li>
                    <li>Any <strong>previous welcome email links</strong> will no longer work.</li>
                </>
            );
        }
        return (
            <>
                <li>A <strong>new welcome email</strong> will be sent to the updated address.</li>
                <li>The staff member must use the <strong>new email</strong> to create their account.</li>
                <li>Any <strong>previous welcome email links</strong> will no longer work.</li>
            </>
        );
    };

    const renderEditEmail = () => (
        <div className="space-y-3">
            <button
                type="button"
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Edit Email Address</h3>

            {/* Same info panel chrome for both account states — only bullets differ. */}
            <WarningPanel variant="info">
                <ul className="list-disc pl-4 space-y-1">
                    {accountIsActive
                        ? renderActiveEmailWarningBullets()
                        : renderPendingEmailWarningBullets()}
                </ul>
            </WarningPanel>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">New email address</label>
                <input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => setEditData(prev => ({
                        ...prev,
                        email: sanitizeEmailInput(e.target.value),
                    }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    placeholder="Enter email address"
                    maxLength={254}
                    autoComplete="off"
                />
                {editData.email?.trim() && !isValidStaffEmailFormat(editData.email) && (
                    <p className="text-xs text-red-500 mt-1">Please enter a valid email address.</p>
                )}
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Confirm new email address
                </label>
                <input
                    type="email"
                    value={editData.confirmEmail || ''}
                    onChange={(e) => setEditData(prev => ({
                        ...prev,
                        confirmEmail: sanitizeEmailInput(e.target.value),
                    }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    placeholder="Re-enter email address"
                    maxLength={254}
                    autoComplete="off"
                />
                {editData.confirmEmail?.trim()
                    && editData.email?.trim().toLowerCase() !== editData.confirmEmail.trim().toLowerCase() && (
                    <p className="text-xs text-red-500 mt-1">Email addresses do not match.</p>
                )}
            </div>

            <div className="flex justify-end space-x-2 pt-2">
                <button
                    type="button"
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={goToConfirm}
                    disabled={!isEmailFormReady()}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                        isEmailFormReady()
                            ? 'bg-violet-600 hover:bg-violet-700'
                            : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                >
                    Review Changes
                </button>
            </div>
        </div>
    );

    // ─── Render: Edit Pathways form ──────────────────────────────────────────
    //
    // Allows the admin to toggle which assessment pathways this clinician
    // can be assigned to. Uses the same checkbox pattern as the setup form.
    // At least one pathway must remain selected (enforced in onChange).

    const renderEditSpecialisms = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Edit Pathways</h3>

            <p className="text-xs text-gray-600">
                Select which assessment pathways this clinician can be assigned to.
                At least one pathway must remain selected.
            </p>

            <div className="grid grid-cols-1 gap-2">
                {getSelectablePathwaySpecialisms(availableOrgModules, editData.pathwaySpecialisms).map((specialism) => {
                    const isSelected = (editData.pathwaySpecialisms || []).includes(specialism);
                    // A pathway is only selectable if the organisation actually runs
                    // that assessment module. Unavailable modules are omitted from the
                    // list (so TOC does not see CYP ADHD). A stale assignment that the
                    // org no longer offers still appears, greyed, so it can be unchecked.
                    const moduleEnabled = availableOrgModules.includes(specialism);
                    return (
                        <button
                            key={specialism}
                            type="button"
                            disabled={!moduleEnabled}
                            title={!moduleEnabled ? 'This pathway is not enabled for your organisation' : undefined}
                            onClick={() => {
                                if (!moduleEnabled) return;
                                setEditData(prev => {
                                    const current = prev.pathwaySpecialisms || [];
                                    if (isSelected) {
                                        const next = current.filter(s => s !== specialism);
                                        if (next.length === 0) return prev;
                                        return { ...prev, pathwaySpecialisms: next };
                                    }
                                    return { ...prev, pathwaySpecialisms: [...current, specialism] };
                                });
                            }}
                            className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                !moduleEnabled
                                    ? 'bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed opacity-70'
                                    : isSelected
                                        ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                            }`}
                        >
                            <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
                                isSelected && moduleEnabled
                                    ? 'bg-violet-600 border-violet-600'
                                    : 'border-gray-300 bg-white'
                            }`}>
                                {isSelected && moduleEnabled && (
                                    <CheckIcon className="w-3 h-3 text-white" />
                                )}
                            </div>
                            <span>{PATHWAY_SPECIALISM_LABELS[specialism] || specialism}</span>
                            {!moduleEnabled && (
                                <span className="ml-2 text-[10px] italic text-gray-400">
                                    Not enabled for your organisation
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={goToConfirm}
                    disabled={!isSpecialismsFormValid() || !computeChangedFields()}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                        isSpecialismsFormValid() && computeChangedFields()
                            ? 'bg-violet-600 hover:bg-violet-700'
                            : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                >
                    Review Changes
                </button>
            </div>
        </div>
    );

    // ─── Render: Disable Account screen ─────────────────────────────────────

    const renderDisableAccount = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Disable Account</h3>

            <p className="text-xs text-gray-600">
                Disabling this account will immediately prevent{' '}
                <span className="font-semibold">
                    {[clinician?.title, clinician?.firstName, clinician?.lastName].filter(Boolean).join(' ') || 'this user'}
                </span>{' '}
                from signing in. If they are currently signed in, they will be blocked
                within seconds. Their data will be preserved.
            </p>

            {/* Assigned-patients warning from dryRun. Disabling does not
                reassign patients, so we surface name + reference number (same
                scrollable list as the account-type block screen) and ask the
                admin to consider reassigning first. The former "outstanding
                clinical reviews" block was removed — it showed raw UIDs and
                unclear section labels that weren't actionable here. */}
            {isDryRunLoading && (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
                    <p className="text-xs text-gray-500">Loading assigned patients...</p>
                </div>
            )}

            {disableDryRunData && !isDryRunLoading && disableDryRunData.patientCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
                    <div className="flex items-start space-x-2">
                        <ExclamationIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                            <p className="font-semibold mb-1">
                                This user has {disableDryRunData.patientCount} patient(s) currently assigned.
                            </p>
                            <p>
                                Disabling this account will not reassign patients. Consider
                                reassigning patients before disabling.
                            </p>
                        </div>
                    </div>
                    {renderAssignedPatientsList(disableDryRunData.patients || [])}
                </div>
            )}

            {/* Optional reason field */}
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Reason (optional)
                </label>
                <textarea
                    value={disableReason}
                    onChange={(e) => setDisableReason(e.target.value)}
                    placeholder="e.g. Left the organisation, security concern..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={2}
                />
            </div>

            {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-xs text-red-700">{submitError}</p>
                </div>
            )}

            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    onClick={handleDisableSubmit}
                    disabled={isSubmitting || isDryRunLoading}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors flex items-center space-x-1 ${
                        isSubmitting || isDryRunLoading
                            ? 'bg-gray-400 cursor-not-allowed opacity-50'
                            : 'bg-red-600 hover:bg-red-700'
                    }`}
                >
                    {isSubmitting && (
                        <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    )}
                    <span>{isSubmitting ? 'Disabling...' : 'Disable Account'}</span>
                </button>
            </div>
        </div>
    );

    // ─── Render: Enable Account screen ────────────────────────────────────────

    const renderEnableAccount = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Enable Account</h3>

            <p className="text-xs text-gray-600">
                Re-enabling this account will allow{' '}
                <span className="font-semibold">
                    {[clinician?.title, clinician?.firstName, clinician?.lastName].filter(Boolean).join(' ') || 'this user'}
                </span>{' '}
                to sign in again. All their previous data and assignments will be intact.
            </p>

            {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-xs text-red-700">{submitError}</p>
                </div>
            )}

            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    onClick={handleEnableSubmit}
                    disabled={isSubmitting}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors flex items-center space-x-1 ${
                        isSubmitting
                            ? 'bg-gray-400 cursor-not-allowed opacity-50'
                            : 'bg-green-600 hover:bg-green-700'
                    }`}
                >
                    {isSubmitting && (
                        <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    )}
                    <span>{isSubmitting ? 'Enabling...' : 'Enable Account'}</span>
                </button>
            </div>
        </div>
    );

    // ─── Render: Edit Account Type screen ─────────────────────────────────────

    const renderEditAccountType = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Change Account Type</h3>

            {/* Current type (read-only) */}
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Current Type</label>
                <div className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                    {getUiUserType(clinician)}
                </div>
            </div>

            {/* New type dropdown */}
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">New Type</label>
                <Select
                    {...DIALOG_SELECT_PORTAL_PROPS}
                    options={getAccountTypeDropdownOptions()}
                    value={selectedAccountType ? { value: selectedAccountType, label: selectedAccountType.label } : null}
                    onChange={(selected) => setSelectedAccountType(selected?.value || null)}
                    placeholder="Select new account type..."
                    className="text-sm"
                    styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({ ...base, minHeight: '36px', borderColor: '#d1d5db', '&:hover': { borderColor: '#7c3aed' } }),
                        option: (base, state) => ({ ...base, fontSize: '0.875rem', backgroundColor: state.isSelected ? '#7c3aed' : state.isFocused ? '#ede9fe' : 'white' }),
                    }}
                />
            </div>

            {/* Gated transition warning */}
            {selectedAccountType && isGatedTransition(
                clinician?.userType,
                clinician?.isAssignableClinician,
                selectedAccountType.userType,
                selectedAccountType.isAssignableClinician,
            ) && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <div className="flex items-start space-x-2">
                        <ExclamationIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-800">
                            Changing from a clinical to a non-clinical role will be blocked
                            if this user has any patients currently assigned. All patients
                            must be reassigned first.
                        </p>
                    </div>
                </div>
            )}

            {/* Last-admin warning */}
            {selectedAccountType && removesAdminStatus(clinician?.userType, selectedAccountType.userType) && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <div className="flex items-start space-x-2">
                        <ExclamationIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-800">
                            This will remove Organisation Administrator privileges. If this
                            is the last admin, the change will be blocked.
                        </p>
                    </div>
                </div>
            )}

            {/* Optional reason */}
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Reason (optional)
                </label>
                <textarea
                    value={accountTypeReason}
                    onChange={(e) => setAccountTypeReason(e.target.value)}
                    placeholder="e.g. Error at account setup, new responsibilities..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                    rows={2}
                />
            </div>

            {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-xs text-red-700">{submitError}</p>
                </div>
            )}

            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    onClick={handleAccountTypeReview}
                    disabled={!selectedAccountType || isSubmitting}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors flex items-center space-x-1 ${
                        !selectedAccountType || isSubmitting
                            ? 'bg-gray-400 cursor-not-allowed opacity-50'
                            : 'bg-violet-600 hover:bg-violet-700'
                    }`}
                >
                    {isSubmitting && (
                        <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    )}
                    <span>{isSubmitting ? 'Applying...' : 'Confirm Change'}</span>
                </button>
            </div>
        </div>
    );

    // Shared scrollable patient list used by Disable Account (warning) and
    // Cannot Change Account Type (hard block). Height-capped so a large caseload
    // cannot grow the modal; each row shows name + reference number (never the
    // raw patient UID) so the admin can find them on the Patients tab.
    const renderAssignedPatientsList = (patients) => {
        if (!patients || patients.length === 0) return null;
        return (
            <div
                data-testid="assigned-patients-list"
                className="max-h-48 overflow-y-auto overscroll-contain border border-gray-200 rounded-md divide-y divide-gray-100 bg-white"
            >
                {patients.map((patient) => (
                    <div
                        key={patient.patientId}
                        className="px-3 py-2 text-xs text-gray-700 flex items-baseline justify-between gap-3"
                    >
                        <span className="min-w-0 break-words font-medium">
                            {patient.patientName}
                        </span>
                        <span className="flex-shrink-0 text-gray-500 tabular-nums">
                            {patient.referenceNumber
                                ? patient.referenceNumber
                                : 'No reference'}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    // ─── Render: Account Type Blocked screen ──────────────────────────────────

    const renderAccountTypeBlocked = () => (
        <div className="space-y-3">
            <button
                onClick={goToEditAccountType}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back
            </button>

            <h3 className="text-base font-semibold text-gray-900">Cannot Change Account Type</h3>

            <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex items-start space-x-2">
                    <BanIcon className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-red-800">
                        <p className="font-semibold mb-1">
                            This user has {accountTypeBlockedPatients.length} patient(s) currently
                            assigned. Their account type cannot be changed to a non-clinical role
                            until all patients have been reassigned to another clinician.
                        </p>
                    </div>
                </div>
            </div>

            {renderAssignedPatientsList(accountTypeBlockedPatients)}

            <div className="flex justify-end pt-2">
                <button
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Close
                </button>
            </div>
        </div>
    );

    // ─── Render: Confirmation screen ─────────────────────────────────────────

    const renderConfirm = () => {
        const changes = computeChangedFields();
        if (!changes) {
            goBackToSummary();
            return null;
        }

        const categoryLabels = {
            name: 'Name & Title',
            role: 'Role',
            registration: 'Registration',
            specialisms: 'Pathways',
            email: 'Email Address',
        };
        const isEmailChange = editCategory === 'email' || changes.email !== undefined;

        return (
            <div className="space-y-3">
                <button
                    type="button"
                    onClick={() => setCurrentStep(`edit-${editCategory}`)}
                    className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
                >
                    &larr; Back to Edit {categoryLabels[editCategory] || ''}
                </button>

                <h3 className="text-base font-semibold text-gray-900">
                    {isEmailChange ? 'Confirm Email Change' : 'Confirm Changes'}
                </h3>

                <p className="text-xs text-gray-600">
                    Please review the following changes for{' '}
                    <span className="font-semibold">
                        {[clinician?.title, clinician?.firstName, clinician?.lastName].filter(Boolean).join(' ') || 'this user'}
                    </span>
                    :
                </p>

                {/* Diff view: shows old -> new for each changed field */}
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
                    {Object.entries(changes).map(([key, newValue]) => {
                        // Format the display value: arrays (e.g. pathwaySpecialisms) are
                        // converted to a comma-separated string of human-readable labels.
                        const displayNewValue = Array.isArray(newValue)
                            ? newValue.map(v => PATHWAY_SPECIALISM_LABELS[v] || v).join(', ')
                            : newValue;

                        return (
                            <div key={key} className="text-xs">
                                <span className="font-medium text-gray-700">{FIELD_LABELS[key]}:</span>
                                <div className="flex flex-wrap items-center gap-x-2 mt-0.5 ml-2">
                                    <span className="text-red-600 line-through break-all">
                                        {getOriginalValue(key) || '(empty)'}
                                    </span>
                                    <span className="text-gray-400">&rarr;</span>
                                    <span className="text-green-700 font-medium break-all">
                                        {displayNewValue || '(empty)'}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Email change guidance — same info chrome; bullets differ by state / self */}
                {isEmailChange && (
                    <WarningPanel variant="info">
                        <ul className="list-disc pl-4 space-y-1">
                            {accountIsActive
                                ? renderActiveEmailWarningBullets({ includeNextLoginNote: true })
                                : renderPendingEmailWarningBullets()}
                        </ul>
                    </WarningPanel>
                )}

                {/* Name change propagation warning */}
                {(changes.title || changes.firstName || changes.lastName) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                        <div className="flex items-start space-x-2">
                            <ExclamationIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-800">
                                Name changes will be propagated to all patient records
                                where this clinician is assigned. This may take a minute.
                            </p>
                        </div>
                    </div>
                )}

                {/* Error message */}
                {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-xs text-red-700">{submitError}</p>
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end space-x-2 pt-2">
                    <button
                        type="button"
                        onClick={goBackToSummary}
                        disabled={isSubmitting}
                        className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors flex items-center space-x-1 ${
                            isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed opacity-50'
                                : 'bg-violet-600 hover:bg-violet-700'
                        }`}
                    >
                        {isSubmitting && (
                            <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        )}
                        <span>
                            {isSubmitting
                                ? 'Saving...'
                                : (isEmailChange ? 'Confirm Change' : 'Confirm Changes')}
                        </span>
                    </button>
                </div>
            </div>
        );
    };

    // ─── Render: Confirm Resend Welcome Email ────────────────────────────────

    const renderConfirmResend = () => {
        // SSO-with-Auth staff get Microsoft wording; password/hybrid keep setup-password copy.
        const isMicrosoftSso = staffAuthMode === 'with_microsoft_sso';
        const displayName = [clinician?.title, clinician?.firstName, clinician?.lastName]
            .filter(Boolean)
            .join(' ') || 'this staff member';

        return (
            <div className="space-y-3">
                <button
                    type="button"
                    onClick={goBackToSummary}
                    className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
                    disabled={isSubmitting}
                >
                    &larr; Back to Summary
                </button>

                <h3 className="text-base font-semibold text-gray-900">Resend Welcome Email</h3>

                <p className="text-xs text-gray-600">
                    This will send a new account setup email to{' '}
                    <span className="font-semibold text-gray-900">{displayName}</span>
                    {clinician?.email ? (
                        <>
                            {' '}(<span className="font-semibold text-gray-900">{clinician.email}</span>)
                        </>
                    ) : null}
                    {isMicrosoftSso
                        ? ' with a link to sign in with Microsoft and complete setup.'
                        : ' with a link to create their account and set their password.'}
                </p>

                <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    {welcomeFirstSent.kind === 'timestamp' ? (
                        <p className="text-xs text-gray-600">
                            <span className="font-medium">First sent:</span>{' '}
                            {formatDateTime(welcomeFirstSent.value)}
                        </p>
                    ) : (
                        <p className="text-xs text-gray-500">{welcomeFirstSent.value}</p>
                    )}
                </div>

                {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-xs text-red-700">{submitError}</p>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <button
                        type="button"
                        onClick={goBackToSummary}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleResendWelcome}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-xs font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Sending...
                            </>
                        ) : (
                            'Send Email'
                        )}
                    </button>
                </div>
            </div>
        );
    };

    // ─── Render: Success screen ──────────────────────────────────────────────

    const renderSuccess = () => (
        <div className="space-y-3 text-center">
            <div className="flex justify-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircleSolid className="h-6 w-6 text-green-600" />
                </div>
            </div>
            {/*
              * Title/body adapt to the operation that just completed:
              *   - email edits use dedicated email copy
              *   - other detail edits (editCategory set) describe the patient
              *     record fan-out
              *   - account-management operations (editCategory null) set a
              *     custom successMessage (e.g. "Account Disabled") and use a
              *     generic body.
              */}
            <h3 className="text-base font-semibold text-gray-900">
                {editCategory === 'email' ? 'Email Address Updated' : successMessage}
            </h3>
            <p className="text-xs text-gray-600">
                {editCategory === 'email'
                    ? (
                        accountIsActive
                            ? (
                                isSelf
                                    ? 'Your sign-in email has been updated. You will be signed out in a moment — please sign in again with your new email address.'
                                    : 'The sign-in email has been updated. Notification emails have been sent where possible.'
                            )
                            : 'The email address has been updated and a new welcome email has been sent.'
                    )
                    : editCategory
                        ? (
                            'The provider details have been updated successfully. Changes have '
                            + 'been propagated to all relevant patient records.'
                        )
                        : 'The operation completed successfully.'}
            </p>
            {submitWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-left">
                    <p className="text-xs text-amber-800">{submitWarning}</p>
                </div>
            )}
            <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors"
            >
                Done
            </button>
        </div>
    );

    // ─── Render: Step router ─────────────────────────────────────────────────

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 'summary':
                return renderSummary();
            case 'edit-name':
                return renderEditName();
            case 'edit-role':
                return renderEditRole();
            case 'edit-registration':
                return renderEditRegistration();
            case 'edit-specialisms':
                return renderEditSpecialisms();
            case 'edit-email':
                return renderEditEmail();
            case 'confirm-resend':
                return renderConfirmResend();
            case 'confirm':
                return renderConfirm();
            case 'disable-account':
                return renderDisableAccount();
            case 'enable-account':
                return renderEnableAccount();
            case 'edit-account-type':
                return renderEditAccountType();
            case 'account-type-blocked':
                return renderAccountTypeBlocked();
            case 'reset-mfa':
                return (
                    <ResetMfaFlow
                        targetUser={{
                            firstName: clinician?.firstName || '',
                            lastName: clinician?.lastName || '',
                            email: clinician?.email || '',
                            isMFASetUp: clinician?.isMFASetUp,
                            mfaMethod: clinician?.mfaMethod,
                        }}
                        targetUserType="clinician"
                        onReset={async () => {
                            setMfaResetLoading(true);
                            setMfaResetError(null);
                            try {
                                const resetMfa = httpsCallable(functions, 'resetUserMfa');
                                const result = await resetMfa({
                                    targetUserId: clinician.clinicianId,
                                    targetUserType: 'clinician',
                                });
                                return result.data;
                            } catch (err) {
                                const message = err?.message || 'Failed to reset two-factor authentication. Please try again.';
                                setMfaResetError(message);
                                throw err;
                            } finally {
                                setMfaResetLoading(false);
                            }
                        }}
                        onBack={() => setCurrentStep('summary')}
                        onFinish={async () => {
                            await refreshProviderAccountsData();
                            setCurrentStep('summary');
                        }}
                        actionLoading={mfaResetLoading}
                        actionError={mfaResetError}
                        clearActionState={() => setMfaResetError(null)}
                    />
                );
            case 'success':
                return renderSuccess();
            default:
                return renderSummary();
        }
    };

    // ─── Modal wrapper ───────────────────────────────────────────────────────

    return (
        <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <AlertDialog.Portal>
                <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
                <AlertDialog.Content
                    className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 md:p-6 rounded-lg shadow-lg max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto z-50 transition-all duration-300 ease-in-out"
                >
                    {/* Accessible but visually hidden title and description for screen readers */}
                    <VisuallyHidden.Root>
                        <AlertDialog.Title>Edit Provider Details</AlertDialog.Title>
                    </VisuallyHidden.Root>
                    <VisuallyHidden.Root>
                        <AlertDialog.Description>
                            Edit the selected provider user's name, role, or registration details.
                        </AlertDialog.Description>
                    </VisuallyHidden.Root>

                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        aria-label="Close"
                    >
                        <XIcon className="h-5 w-5" />
                    </button>

                    {/*
                      Parent clears `clinician` when the dialog closes (`editingClinician` → null).
                      This form stays mounted so Radix can play the exit animation, so we must not
                      render step content that dereferences `clinician` until it is non-null again.
                    */}
                    {clinician ? renderCurrentStep() : null}
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
}

export default ProviderEditUserForm;
